import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import WebSocketService from "../../../websockets/WebSocketService";
import { apiFetch as fetch } from "apiClient";

// Shapes:
// - PortfolioWithResultDTO: { row: { stockSymbol, currentValue, ... }, resultDate: 'yyyy-MM-dd' }
// - ResultDateSimpleDTO:   { symbol, resultDate: 'yyyy-MM-dd' }

const ResultDatesContext = createContext(null);

function keyFor(symbol, date) {
    return `${symbol}__${date}`;
}

export function ResultDatesProvider({ children }) {
    const [pfUpcoming, setPfUpcoming] = useState([]);
    const [pfAll, setPfAll] = useState([]);
    const [allUpcoming, setAllUpcoming] = useState([]);
    const [allAll, setAllAll] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Helpers to upsert arrays by (symbol+date)
    const upsertList = (prev, items, getSymbol, getDate) => {
        const map = new Map(prev.map(it => [keyFor(getSymbol(it), getDate(it)), it]));
        for (const it of items) {
            map.set(keyFor(getSymbol(it), getDate(it)), it);
        }
        return Array.from(map.values());
    };

    const loadAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const [
                resPfUpcoming,
                resPfAll,
                resAllUpcoming,
                resAllAll,
            ] = await Promise.all([
                fetch('/api/result-dates/portfolio/upcoming'),
                fetch('/api/result-dates/portfolio/all'),
                fetch('/api/result-dates/all/upcoming'),
                fetch('/api/result-dates/all'),
            ]);

            const [pfU, pfA, aU, aA] = await Promise.all([
                resPfUpcoming.ok ? resPfUpcoming.json() : [],
                resPfAll.ok ? resPfAll.json() : [],
                resAllUpcoming.ok ? resAllUpcoming.json() : [],
                resAllAll.ok ? resAllAll.json() : [],
            ]);

            setPfUpcoming(Array.isArray(pfU) ? pfU : []);
            setPfAll(Array.isArray(pfA) ? pfA : []);
            setAllUpcoming(Array.isArray(aU) ? aU : []);
            setAllAll(Array.isArray(aA) ? aA : []);
        } catch (e) {
            console.error(e);
            setError(String(e?.message || e));
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        loadAll();
        const unsub1 = WebSocketService.subscribe("resultDates", (msg) => {
            // payload: { type: "resultDates", data: [{ symbol, resultDate }, ...] }
            const payload = Array.isArray(msg?.data) ? msg.data : (msg?.data ? [msg.data] : []);
            const today = new Date().toISOString().slice(0,10);
            setAllAll(prev => upsertList(prev, payload, x => x.symbol, x => x.resultDate));
            setAllUpcoming(prev => upsertList(
                prev,
                payload.filter(x => (x.resultDate && x.resultDate >= today)),
                x => x.symbol, x => x.resultDate
            ));
        });
        const unsub2 = WebSocketService.subscribe("resultDatesPF", (msg) => {
            // payload: { type: "resultDatesPF", data: [PortfolioWithResultDTO, ...] }
            const payload = Array.isArray(msg?.data) ? msg.data : (msg?.data ? [msg.data] : []);
            const today = new Date().toISOString().slice(0,10);
            setPfAll(prev => upsertList(prev, payload, x => x?.row?.stockSymbol, x => x?.resultDate));
            setPfUpcoming(prev => upsertList(
                prev,
                payload.filter(x => (x?.resultDate && x.resultDate >= today)),
                x => x?.row?.stockSymbol, x => x?.resultDate
            ));
        });
        return () => { unsub1 && unsub1(); unsub2 && unsub2(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(() => ({
        pfUpcoming, pfAll, allUpcoming, allAll, loading, error, refresh: loadAll
    }), [pfUpcoming, pfAll, allUpcoming, allAll, loading, error]);

    return <ResultDatesContext.Provider value={value}>{children}</ResultDatesContext.Provider>;
}

export function useResultDates() {
    const ctx = useContext(ResultDatesContext);
    if (!ctx) throw new Error("useResultDates must be used within a ResultDatesProvider");
    return ctx;
}

export default ResultDatesContext;
